import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

const DisclaimerModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    // Check sessionStorage instead of localStorage (shows every time browser opens)
    const agreed = sessionStorage.getItem('cm-peptides-disclaimer-agreed');
    if (!agreed) {
      setIsOpen(true);
    } else {
      setHasAgreed(true);
    }
  }, []);

  const handleAgree = () => {
    if (isChecked) {
      // Use sessionStorage so disclaimer shows again when browser is reopened
      sessionStorage.setItem('cm-peptides-disclaimer-agreed', 'true');
      setHasAgreed(true);
      setIsOpen(false);
    }
  };

  if (hasAgreed || !isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] modal-overlay flex items-center justify-center p-4">
      <div className="modal-content max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#D4A373] to-[#C78C50] text-[#0C0A09] p-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-8 w-8" />
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Important Legal Notice</h2>
              <p className="text-[#0C0A09]/80 text-sm">Please read and accept the terms to continue</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" style={{ backgroundColor: '#1A1513', color: '#F7F3EE' }}>
          <div className="border-l-4 p-4 rounded" style={{ backgroundColor: '#362B25', borderColor: '#D4A373' }}>
            <p className="font-semibold" style={{ color: '#D4A373' }}>
              ⚠️ RESEARCH PURPOSES ONLY - NOT FOR HUMAN CONSUMPTION
            </p>
          </div>

          <div className="prose prose-sm max-w-none space-y-4" style={{ color: '#B5A79E' }}>
            <h3 className="text-lg font-bold" style={{ color: '#F7F3EE' }}>Terms and Conditions of Sale</h3>
            
            <p className="leading-relaxed">
              By accessing and using this website and purchasing products from CM Peptides ("Company," "we," "us," or "our"), 
              you ("User," "Customer," or "you") acknowledge that you have read, understood, and agree to be bound by the 
              following terms and conditions.
            </p>

            <h4 className="font-bold text-gray-900 mt-4">1. RESEARCH CHEMICALS - NOT FOR HUMAN USE</h4>
            <p className="leading-relaxed">
              All peptides, compounds, and products sold by CM Peptides are intended STRICTLY FOR LABORATORY RESEARCH PURPOSES ONLY. 
              These products are NOT drugs, NOT dietary supplements, NOT food additives, and are NOT intended for human consumption, 
              therapeutic use, clinical use, or any form of in-vivo application in humans or animals. These research chemicals have not 
              been approved by the FDA, Health Canada, or any other regulatory authority for human or veterinary use.
            </p>

            <h4 className="font-bold text-gray-900 mt-4">2. PURCHASER QUALIFICATIONS</h4>
            <p className="leading-relaxed">
              By purchasing from CM Peptides, you represent and warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are at least 18 years of age or the age of majority in your jurisdiction</li>
              <li>You are a qualified research professional, scientist, or institution conducting legitimate scientific research</li>
              <li>You possess the appropriate licenses, permits, and qualifications required in your jurisdiction to purchase and handle research chemicals</li>
              <li>You will use the products solely for lawful research purposes in a proper laboratory setting</li>
              <li>You will NOT use these products for human consumption, self-experimentation, bodybuilding, performance enhancement, or any therapeutic purpose</li>
            </ul>

            <h4 className="font-bold text-gray-900 mt-4">3. HEALTH AND SAFETY WARNINGS</h4>
            <p className="leading-relaxed">
              Research peptides and chemicals may be HAZARDOUS to your health if misused. These substances:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Have not undergone safety testing for human consumption</li>
              <li>May cause serious adverse health effects, injury, or death if consumed or improperly handled</li>
              <li>May interact dangerously with medications, supplements, or pre-existing health conditions</li>
              <li>Should only be handled by trained professionals using appropriate safety equipment and protocols</li>
            </ul>

            <h4 className="font-bold text-gray-900 mt-4">4. NO MEDICAL CLAIMS OR ADVICE</h4>
            <p className="leading-relaxed">
              CM Peptides does not make any claims regarding the therapeutic benefits, medical applications, or health effects of any products. 
              Any information provided on this website is for research and educational purposes only and should not be construed as medical advice. 
              Always consult with a licensed healthcare professional before making any health-related decisions.
            </p>

            <h4 className="font-bold text-gray-900 mt-4">5. ASSUMPTION OF RISK</h4>
            <p className="leading-relaxed">
              You acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Purchase and use of research chemicals carries inherent risks</li>
              <li>You assume all risks associated with the purchase, possession, handling, and use of our products</li>
              <li>You are solely responsible for compliance with all applicable laws and regulations in your jurisdiction</li>
              <li>You will indemnify and hold harmless CM Peptides from any claims, damages, or liabilities arising from your use or misuse of our products</li>
            </ul>

            <h4 className="font-bold text-gray-900 mt-4">6. LIMITATION OF LIABILITY</h4>
            <p className="leading-relaxed">
              To the maximum extent permitted by law, CM Peptides, its owners, employees, and affiliates shall not be liable for any direct, 
              indirect, incidental, consequential, or punitive damages arising from the use, misuse, or inability to use our products, including 
              but not limited to personal injury, health complications, property damage, or death. Our maximum liability shall not exceed the 
              purchase price of the product(s) in question.
            </p>

            <h4 className="font-bold text-gray-900 mt-4">7. LEGAL COMPLIANCE</h4>
            <p className="leading-relaxed">
              You are responsible for ensuring that your purchase and use of our products complies with all applicable federal, state, and local 
              laws and regulations. Certain peptides may be controlled substances in some jurisdictions. We reserve the right to refuse service 
              to anyone and to cancel orders at our discretion.
            </p>

            <h4 className="font-bold text-gray-900 mt-4">8. NO WARRANTIES</h4>
            <p className="leading-relaxed">
              All products are provided "AS IS" without any warranty of any kind, express or implied, including but not limited to warranties 
              of merchantability, fitness for a particular purpose, or non-infringement. We do not guarantee specific research outcomes or results.
            </p>

            <h4 className="font-bold text-gray-900 mt-4">9. ACKNOWLEDGMENT OF UNDERSTANDING</h4>
            <p className="leading-relaxed font-semibold">
              BY CHECKING THE BOX BELOW AND CLICKING "I AGREE," YOU ACKNOWLEDGE THAT:
            </p>
            <ul className="list-disc pl-6 space-y-2 font-medium">
              <li>You have read and understood these terms and conditions in their entirety</li>
              <li>You understand that these products are for research purposes only and not for human consumption</li>
              <li>You accept all risks associated with the purchase and use of research chemicals</li>
              <li>You agree to indemnify and hold harmless CM Peptides from any liability</li>
              <li>You are legally qualified to purchase and possess these research chemicals in your jurisdiction</li>
            </ul>
          </div>

          <div className="border-t-2 pt-6" style={{ borderColor: '#362B25' }}>
            <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)' }}>
              <p className="font-bold text-center" style={{ color: '#FCA5A5' }}>
                ⚠️ WARNING: Misuse of research chemicals can result in serious injury or death.
                If you do not accept these terms, you must leave this website immediately.
              </p>
            </div>
          </div>
        </div>

        {/* Sticky Agreement Footer - Always Visible */}
        <div className="border-t-2 p-5 space-y-4" style={{ backgroundColor: '#0C0A09', borderColor: '#D4A373' }}>
          <label
            htmlFor="disclaimer-check"
            className="flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-all"
            style={{
              backgroundColor: isChecked ? 'rgba(212, 163, 115, 0.15)' : '#1A1513',
              border: isChecked ? '2px solid #D4A373' : '2px solid #362B25'
            }}
          >
            <Checkbox
              id="disclaimer-check"
              checked={isChecked}
              onCheckedChange={setIsChecked}
              className="mt-1 border-[#D4A373] data-[state=checked]:bg-[#D4A373] data-[state=checked]:border-[#D4A373]"
            />
            <span className="text-sm leading-relaxed flex-1" style={{ color: '#F7F3EE' }}>
              <strong style={{ color: '#D4A373' }}>I confirm</strong> that I am 18 years or older, a qualified research professional, I have read and understood all terms above, and I agree that all products are for laboratory research purposes only and NOT for human consumption.
            </span>
          </label>

          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <a
              href="https://www.google.com"
              className="text-sm hover:underline text-center sm:text-left order-2 sm:order-1"
              style={{ color: '#B5A79E' }}
              onClick={(e) => {
                e.preventDefault();
                window.location.href = 'https://www.google.com';
              }}
            >
              I do not agree — Leave site
            </a>
            <button
              onClick={handleAgree}
              disabled={!isChecked}
              data-testid="disclaimer-agree-btn"
              className={`order-1 sm:order-2 flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold text-base uppercase tracking-wide transition-all ${
                isChecked
                  ? 'shadow-lg hover:scale-[1.02] cursor-pointer'
                  : 'cursor-not-allowed opacity-60'
              }`}
              style={{
                backgroundColor: isChecked ? '#D4A373' : '#362B25',
                color: isChecked ? '#0C0A09' : '#B5A79E',
                boxShadow: isChecked ? '0 0 24px rgba(212, 163, 115, 0.4)' : 'none',
                minWidth: '260px'
              }}
            >
              <CheckCircle className="h-5 w-5" />
              {isChecked ? 'I Agree — Enter Site' : 'Check the box above to continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
